import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import {
    KAL_DIALOG_DATA,
    KalAutocompleteOption,
    KalDate,
    KalDialogConfig,
    KalDialogRef,
    KalDialogService
} from '@kalidea/kaligraphi';
import gql from 'graphql-tag';
import cloneDeep from 'lodash-es/cloneDeep';
import get from 'lodash-es/get';
import { filter, map, tap } from 'rxjs/operators';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { BaseComponent } from 'src/app/_core/tools/base.component';
import { DocumentBankListDialogComponent } from 'src/app/_shared/dialogs/document-bank-dialog/document-bank-list-dialog/document-bank-list-dialog.component';
import { ImageBankListDialogComponent } from 'src/app/_shared/dialogs/image-bank-dialog/image-bank-list-dialog/image-bank-list-dialog.component';
import { UserState } from 'src/app/_user/store/user/user.state';
import { arrayNotEmpty } from 'src/app/utils/array.utils';
import { createNewFile } from 'src/app/utils/file.utils';
import { wheneverEnd } from 'src/app/utils/operators.utils';
import { FormValidator } from 'src/app/validators/form-validator';
import { environment } from 'src/environments/environment';
import {
    EditerArticleMonEspaceCse,
    FichierModel,
    FichierModelInput,
    MutationGetListeProfilsMonEspaceCseArgs,
    MutationGetListeTagsBibliothequeImageArgs,
    ProfilEspaceCse
} from 'src/graphql/generated-types';

export interface PublishingOptionsMonEspaceCSEDialogData {
    applicationPublication?: EditerArticleMonEspaceCse;
}

const TITLE_MAXLENGTH = 97;
const NOTIFICATION_TITLE_MAXLENGTH = 32;
const NOTIFICATION_CONTENT_MAXLENGTH = 97;
const MAX_TAGS_LENGTH = 2;

@Component({
    selector: 'app-publishing-option-mon-espace-cse-dialog',
    templateUrl: './publishing-option-mon-espace-cse-dialog.component.html',
    styleUrls: ['./publishing-option-mon-espace-cse-dialog.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PublishingOptionsMonEspaceCSEDialogComponent extends BaseComponent implements OnInit {

    form: UntypedFormGroup;

    tagsListDatasource: KalAutocompleteOption[] = [];
    addTagControl = new UntypedFormControl();
    displayTagInput: boolean;
    tagsList: string[] = [];

    isFormDateDisplayed = false;

    picture: FichierModel = null;
    invalidPicture = false;

    showTruncatedTitleWarning = false;

    profilsList: ProfilEspaceCse[];

    unlinkedDocumentsList: FichierModel[] = [];
    addedDocumentsList: FichierModelInput[] = [];
    documentsList: FichierModel[] = [];

    applicationPublication: EditerArticleMonEspaceCse;

    constructor(private dialogRef: KalDialogRef<PublishingOptionsMonEspaceCSEDialogComponent>,
                private fb: UntypedFormBuilder,
                private readonly graphqlService: GraphqlService,
                protected readonly cdr: ChangeDetectorRef,
                private readonly dialogService: KalDialogService,
                @Inject(KAL_DIALOG_DATA) public data: PublishingOptionsMonEspaceCSEDialogData) {
        super(cdr);
    }

    get submitButtonText(): string {
        return this.applicationPublication.IdContent ? 'Modifier' : 'Publier';
    }

    get pictureUrl(): string {
        return this.picture?.Url || this.form.get('picture')?.value?.fileBlob;
    }

    get maxFileSize(): number {
        return this.snapshot(UserState.maxFileSize);
    }

    get titleMaxLength(): number {
        return TITLE_MAXLENGTH;
    }

    submit(): void {
        if (this.form.invalid || this.invalidPicture) {
            return;
        }

        this.savePublication();
    }

    close(): void {
        this.dialogRef.close();
    }

    togglePublicationDates(): void {
        this.isFormDateDisplayed = !this.isFormDateDisplayed;

        if (!this.isFormDateDisplayed) {
            this.form.get('publicationDates').patchValue({startDate: null, endDate: null});
        }
    }

    toggleDisplayTagInput(): void {
        this.displayTagInput = !this.displayTagInput;
    }

    pickTag(tag: string): void {
        if (this.tagsList.length >= MAX_TAGS_LENGTH || !tag) {
            return;
        }
        this.tagsList.push(tag);
        this.addTagControl.patchValue(null, {emitEvent: false});
        if (this.tagsList.length >= MAX_TAGS_LENGTH) {
            this.addTagControl.disable();
        }
    }

    deleteTag(index: number) {
        this.tagsList.splice(index, 1);
        if (this.tagsList.length < MAX_TAGS_LENGTH) {
            this.addTagControl.enable();
        }
    }

    hideTruncatedTitleWarning() {
        this.showTruncatedTitleWarning = false;
    }

    openImagesBankDialog(): void {
        const config = new KalDialogConfig({
            title: 'Ajouter une image',
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop',
        });
        const dialogRef = this.dialogService.open(ImageBankListDialogComponent, config);

        this.watch$ = dialogRef.afterClosed.pipe(
            filter(result => result && (!!result.selectedPicture || !!result.uploadedPicture)),
            tap(({selectedPicture, uploadedPicture}) => {
                this.picture = createNewFile(selectedPicture || uploadedPicture);
                this.form.get('picture').patchValue(this.picture);
                this.invalidPicture = false;
                this.cdr.markForCheck();
            })
        );
    }

    deletePicture(): void {
        this.form.get('picture').patchValue(null);
        this.picture = null;
    }

    openDocumentsBankDialog(): void {
        const config = new KalDialogConfig({
            title: 'Ajouter un document',
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop',
        });
        const dialogRef = this.dialogService.open(DocumentBankListDialogComponent, config);

        this.watch$ = dialogRef.afterClosed.pipe(
            filter(result => result?.selectedDocument),
            tap(({selectedDocument}) => {
                const {IdFile, Extension, Filename, Url, Token} = selectedDocument;

                // transform selectedDocument of type FichierCompletModel to FichierModelInput
                const document = {
                    IdFile,
                    Extension,
                    Filename,
                    Url,
                    Token
                } as FichierModelInput;

                // check if the document isn't existing on the list
                if (!this.documentsList.some(d => d.IdFile === document.IdFile)) {
                    this.addedDocumentsList.push(document);
                    this.documentsList.push(document);
                    this.cdr.markForCheck();
                }

                // check if the document was unlinked
                const unlinkedDocumentIndex = this.unlinkedDocumentsList.findIndex(d => d.IdFile === selectedDocument.IdFile);
                if (unlinkedDocumentIndex > -1) {
                    this.unlinkedDocumentsList.splice(unlinkedDocumentIndex, 1);
                }
            })
        );
    }

    unlinkDocument(document: FichierModel): void {
        const documentIndex = this.addedDocumentsList.findIndex(d => d.IdFile === document.IdFile);

        // if it's a file that the user just picked up, remove it from added documents list
        if (documentIndex > -1) {
            this.addedDocumentsList.splice(documentIndex, 1);
        } else {
            this.unlinkedDocumentsList.push(document);
        }

        this.removeDocumentFromDisplay(document);
    }

    private removeDocumentFromDisplay(document: FichierModel): void {
        const documentIndex = this.documentsList.findIndex(d => d.IdFile === document.IdFile);
        this.documentsList.splice(documentIndex, 1);

        this.cdr.markForCheck();
    }

    private savePublication() {
        const formValues = this.form.value;

        this.dialogRef.close({
            applicationPublication: {
                ...this.applicationPublication,
                Titre: formValues.title,
                Contenu: formValues.content,
                ListeDocumentLieFile: {FichierModel: (arrayNotEmpty(this.addedDocumentsList) ? this.addedDocumentsList : [])},
                ListeDocumentsToDelete: {string: this.unlinkedDocumentsList.map((doc) => doc.IdFileAliznet)},
                Tags: {string: (arrayNotEmpty(this.tagsList) ? this.tagsList : [])},
                DateDebut: formValues.publicationDates.startDate,
                DateFin: formValues.publicationDates.endDate,
                IsPublie: true,
                IdAuteur: '' + formValues.authorId,
                IsUne: formValues.isTopArticle,
                SendNotification: formValues.sendNotification,
                TitreNotification: formValues.notificationTitle,
                ContenuNotification: formValues.notificationContent,
                IllustrationFile: formValues.picture
            }
        });

    }

    private loadLists() {
        type mutation = 'GetListeProfilsMonEspaceCSE' | 'GetListeTagsBibliothequeImage';
        type types = MutationGetListeProfilsMonEspaceCseArgs | MutationGetListeTagsBibliothequeImageArgs;
        return this.graphqlService.mutateMultiple<mutation, types>({
            mutation: gql`mutation
                loadLists($contexte: ContextInfosInput) {
                    GetListeProfilsMonEspaceCSE(contexte: $contexte)
                    {
                        ProfilEspaceCSE
                        {
                            IdProfile
                            NomProfile
                        }
                    }
                    GetListeTagsBibliothequeImage(contexte: $contexte)
                    {
                        string
                    }
                }
            `
        }).pipe(
            map((results) => {
                return {
                    profilsList: results.GetListeProfilsMonEspaceCSE.ProfilEspaceCSE,
                    tagsList: results.GetListeTagsBibliothequeImage?.string.map(t => ({
                        value: t,
                        label: t
                    })) || []
                };
            })
        );
    }

    private initSubscription(): void {
        const {
            notificationTitle,
            notificationContent
        } = this.form.controls;

        this.watch$ = this.form.get('sendNotification').valueChanges.pipe(
            tap(sendNotification => {
                if (!sendNotification) {
                    notificationTitle.clearValidators();
                    notificationContent.clearValidators();
                } else {
                    notificationTitle.setValidators(
                        [Validators.maxLength(NOTIFICATION_TITLE_MAXLENGTH), FormValidator.isNotNull]
                    );

                    notificationContent.setValidators(
                        [Validators.maxLength(NOTIFICATION_CONTENT_MAXLENGTH), FormValidator.isNotNull]
                    );
                }

                notificationTitle.updateValueAndValidity({emitEvent: false});
                notificationContent.updateValueAndValidity({emitEvent: false});
            })
        );
    }

    ngOnInit() {
        this.applicationPublication = cloneDeep(this.data.applicationPublication);

        if (!!this.applicationPublication.DateDebut || !!this.applicationPublication.DateFin) {
            this.isFormDateDisplayed = true;
        }

        const {
            Titre,
            Contenu,
            IllustrationFile,
            DateDebut,
            DateFin,
            IsUne,
            SendNotification,
            TitreNotification,
            ContenuNotification
        } = this.applicationPublication;

        this.form = this.fb.group({
            title: [Titre.slice(0, TITLE_MAXLENGTH), [FormValidator.isNotNull, Validators.maxLength(TITLE_MAXLENGTH)]],
            content: [Contenu],
            picture: [IllustrationFile, FormValidator.isNotNull],
            publicationDates: [{
                startDate: DateDebut ? new KalDate(DateDebut, environment.date.format.isoQuery) : null,
                endDate: DateFin ? new KalDate(DateFin, environment.date.format.isoQuery) : null
            }],
            authorId: [null, FormValidator.isNotNull],
            isTopArticle: [IsUne],
            sendNotification: [SendNotification],
            notificationTitle: [TitreNotification.slice(0, NOTIFICATION_TITLE_MAXLENGTH), Validators.maxLength(NOTIFICATION_TITLE_MAXLENGTH)],
            notificationContent: [ContenuNotification.slice(0, NOTIFICATION_CONTENT_MAXLENGTH), Validators.maxLength(NOTIFICATION_CONTENT_MAXLENGTH)]
        });

        this.initSubscription();

        // Si le titre à été tronqué, on affiche le message d'alerte
        this.showTruncatedTitleWarning = this.applicationPublication.Titre.length > TITLE_MAXLENGTH;

        // Récup des tags
        this.tagsList = this.applicationPublication.Tags?.string || [];
        this.displayTagInput = this.tagsList.length > 0;

        // Récup de l'image pour l'afficher
        this.picture = this.applicationPublication.IllustrationFile;

        // Récupération des documents liés pour afficher la liste
        this.documentsList = cloneDeep(get(this.applicationPublication.ListeDocumentLieFile, 'FichierModel', []));
        // Récupération de la liste des profils de publication et des tags
        this.loading = true;
        this.watch$ = this.loadLists().pipe(tap((lists) => {
                this.profilsList = lists.profilsList;
                this.tagsListDatasource = lists.tagsList;
                this.form.patchValue({authorId: this.applicationPublication.IdAuteur});
            }),
            wheneverEnd(() => this.loading = false)
        );

    }
}
